import React from 'react';
import { SlidersHorizontalIcon, RotateCcwIcon } from 'lucide-react';
import type { VehicleFilters } from '../types/vehicle';
import { emptyFilters } from '../types/vehicle';
import { CheckboxField, Label } from './ui/Field';
import { useVehicles } from '../contexts/VehiclesContext';
const control = 'w-full rounded-xl border border-line bg-surface2/70 px-3 h-10 text-sm text-white placeholder:text-mist/70 transition-colors duration-150 ease-smooth focus:border-neon';
function Select({
  id,
  label,
  value,
  options,
  onChange
}: {
  id: string;
  label: string;
  value: string;
  options: string[];
  onChange: (v: string) => void;
}) {
  return <div data-id="element-31" data-mp="3vitgy3smdye2">
      <Label htmlFor={id} data-id="element-32" data-mp="3vijqyg7krr8d">{label}</Label>
      <select id={id} value={value} onChange={e => onChange(e.target.value)} className={control} data-id="element-33" data-mp="3qljki46tcmgq">
        <option value="" data-id="element-34" data-mp="2174y3qj9pboe">Todos</option>
        {options.map(o => <option key={o} value={o} data-id="element-35" data-mp="1w8tlx95i2p30">
            {o}
          </option>)}
      </select>
    </div>;
}
function Num({
  id,
  label,
  value,
  placeholder,
  onChange
}: {
  id: string;
  label: string;
  value: string;
  placeholder: string;
  onChange: (v: string) => void;
}) {
  return <div data-id="element-36" data-mp="30siv9pzok9dn">
      <Label htmlFor={id} data-id="element-37" data-mp="30ssf0vu4tkaw">{label}</Label>
      <input id={id} type="number" min={0} inputMode="numeric" value={value} placeholder={placeholder} onChange={e => onChange(e.target.value)} className={control} data-id="element-38" data-mp="35kad9c4rt6s6" />
    </div>;
}
export function CatalogFilters({
  filters,
  onChange,
  onReset
}: {
  filters: VehicleFilters;
  onChange: (patch: Partial<VehicleFilters>) => void;
  onReset: () => void;
}) {
  const {
    vehicles,
    marcas,
    modelos,
    tipos,
    combustibles
  } = useVehicles();
  const isDirty = JSON.stringify({
    ...filters,
    sort: 'destacados'
  }) !== JSON.stringify(emptyFilters);
  return <div className="rounded-2xl border border-line bg-surface2/40 p-5" data-id="element-39" data-mp="1u4lp4hfs61ps">
      <div className="mb-5 flex items-center justify-between" data-id="element-40" data-mp="1u6yg1k3inmez">
        <h2 className="flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-white" data-id="element-41" data-mp="2xgkq9r79qunb">
          <SlidersHorizontalIcon className="h-4 w-4 text-neon" aria-hidden="true" data-id="element-42" data-mp="2siix2dg4pey4" />
          Filtros
        </h2>
        {isDirty ? <button type="button" onClick={onReset} className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-neon-soft hover:text-white" data-id="element-43" data-mp="31hmhpluy22eq">
            <RotateCcwIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-44" data-mp="3bftya3lbmig7" />
            Limpiar
          </button> : null}
      </div>

      <div className="space-y-4" data-id="element-45" data-mp="3p65rk5tn6n8x">
        <div data-id="element-46" data-mp="3p9bcr9z5h4zd">
          <Label htmlFor="f-q" data-id="element-47" data-mp="3l5ls2vllj7jm">Palabra clave</Label>
          <input id="f-q" type="search" value={filters.q} placeholder="Marca, modelo, versión…" onChange={e => onChange({
          q: e.target.value
        })} className={control} data-id="element-48" data-mp="3l6wvuvf1kfaq" />
        </div>

        <Select id="f-marca" label="Marca" value={filters.marca} options={marcas} onChange={v => onChange({
        marca: v,
        modelo: ''
      })} data-id="element-49" data-mp="hmz63aujvl9n" />
        <Select id="f-modelo" label="Modelo" value={filters.modelo} options={filters.marca ? Array.from(new Set(vehicles.filter(v => v.marca === filters.marca).map(v => v.modelo))).sort() : modelos} onChange={v => onChange({
        modelo: v
      })} data-id="element-50" data-mp="7orz9ktz6jld" />

        <div className="grid grid-cols-2 gap-3" data-id="element-51" data-mp="3l1lc3ivctjgc">
          <Num id="f-anio-desde" label="Año desde" value={filters.anioDesde} placeholder="2015" onChange={v => onChange({
          anioDesde: v
        })} data-id="element-52" data-mp="3q3kdin7ja6an" />
          <Num id="f-anio-hasta" label="Año hasta" value={filters.anioHasta} placeholder="2026" onChange={v => onChange({
          anioHasta: v
        })} data-id="element-53" data-mp="3g3tlqtacy5e9" />
        </div>

        <div className="grid grid-cols-2 gap-3" data-id="element-54" data-mp="344eg03uukpnn">
          <Num id="f-precio-min" label="Precio mín." value={filters.precioMin} placeholder="USD 10.000" onChange={v => onChange({
          precioMin: v
        })} data-id="element-55" data-mp="2zaa7hg5xo9kg" />
          <Num id="f-precio-max" label="Precio máx." value={filters.precioMax} placeholder="USD 90.000" onChange={v => onChange({
          precioMax: v
        })} data-id="element-56" data-mp="2qc8cpicsmd5m" />
        </div>

        <Num id="f-km-max" label="Kilometraje máximo" value={filters.kmMax} placeholder="80.000 km" onChange={v => onChange({
        kmMax: v
      })} data-id="element-57" data-mp="2geamipbdi6ad" />

        <Select id="f-tipo" label="Tipo de vehículo" value={filters.tipo} options={tipos} onChange={v => onChange({
        tipo: v
      })} data-id="element-58" data-mp="9m04i2395ujo" />
        <Select id="f-estado" label="Estado" value={filters.estado} options={['Usado', '0KM']} onChange={v => onChange({
        estado: v
      })} data-id="element-59" data-mp="i17irjv4s9v8" />
        <Select id="f-combustible" label="Combustible" value={filters.combustible} options={combustibles} onChange={v => onChange({
        combustible: v
      })} data-id="element-60" data-mp="qxok5vdp0tds" />
        <Select id="f-transmision" label="Transmisión" value={filters.transmision} options={['Manual', 'Automática']} onChange={v => onChange({
        transmision: v
      })} data-id="element-61" data-mp="35rmd872w1vo" />

        <fieldset className="space-y-2.5 border-t border-line/70 pt-4" data-id="element-62" data-mp="d47cp8ya7744">
          <legend className="mb-1 text-[12px] font-semibold uppercase tracking-wide text-mist" data-id="element-63" data-mp="1jjzv9zyxecou">
            Condiciones
          </legend>
          <CheckboxField id="f-financiacion" label="Financiación disponible" checked={filters.financiacion} onChange={v => onChange({
          financiacion: v
        })} data-id="element-64" data-mp="1oi1gorjtqo3h" />
          <CheckboxField id="f-permuta" label="Permuta aceptada" checked={filters.permuta} onChange={v => onChange({
          permuta: v
        })} data-id="element-65" data-mp="tvcaxhg279rj" />
          <CheckboxField id="f-oportunidad" label="Solo oportunidades" checked={filters.oportunidad} onChange={v => onChange({
          oportunidad: v
        })} data-id="element-66" data-mp="13vcvf6pnzvxb" />
        </fieldset>
      </div>
    </div>;
}